import type { ReactNode } from 'react';

/**
 * Controlled collapse primitive — the shared mechanics behind the app's
 * hand-rolled collapsible sections (the automation panel's SectionCard, the
 * AutomationList moment groups, and the queue StepSection all reimplemented
 * this same `<section><button aria-expanded>…</button>{open && <body>}</section>`
 * with a ▾/▸ chevron).
 *
 * It owns NO state: the caller passes `open` + `onToggle` so each site keeps its
 * own state model (an internal boolean, a per-group map, a localStorage-backed
 * flag). Every visual detail is threaded through as a className/testId so a
 * migrated call site renders byte-identically to its hand-rolled version — the
 * primitive only removes the duplicated toggle wiring, not the styling.
 */
export function Collapsible({
  open,
  onToggle,
  header,
  children,
  chevronPosition = 'start',
  className,
  headerClassName,
  bodyClassName,
  chevronClassName,
  testId,
  headerTestId,
}: {
  open: boolean;
  onToggle: () => void;
  /** Header content (label, summary, counts) rendered inside the toggle button,
   *  next to the chevron. */
  header: ReactNode;
  /** Body — mounted only when `open`, matching the hand-rolled `{open && …}`. */
  children: ReactNode;
  chevronPosition?: 'start' | 'end';
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  chevronClassName?: string;
  testId?: string;
  headerTestId?: string;
}) {
  const chevron = (
    <span className={chevronClassName} aria-hidden>
      {open ? '▾' : '▸'}
    </span>
  );
  return (
    <section className={className} data-open={open} data-testid={testId}>
      <button
        type="button"
        className={headerClassName}
        aria-expanded={open}
        data-testid={headerTestId}
        onClick={onToggle}
      >
        {chevronPosition === 'start' && chevron}
        {header}
        {chevronPosition === 'end' && chevron}
      </button>
      {open && <div className={bodyClassName}>{children}</div>}
    </section>
  );
}
